// Sidebar (desktop ≥ lg) — тёмная холодная «панель-пульт».
// Collapsed 64px ↔ Expanded 248px. Persist через _layout-state.
// Активный пункт — приподнятый тактильный чип с боковым синим акцентом.
// Дизайн-язык «Картотека» §3 / §8.A · эталон design-lab/home-8-synthesis.html.

import { Button } from "@studyhub/ui/components/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@studyhub/ui/components/collapsible";
import {
	IconTooltip,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@studyhub/ui/components/tooltip";
import {
	IconArrowRight,
	IconChevronDown,
	IconChevronLeft,
	IconChevronRight,
	IconFlame,
	IconPlus,
	IconSparkles,
} from "@tabler/icons-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { forwardRef, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { isDimmedPresentation, ModuleNavMarker } from "@/components/modules/module-nav-marker";
import {
	ALL_NAV_ITEMS,
	GUEST_SIDEBAR_GROUPS,
	isNavItemActive,
	SIDEBAR_GROUPS,
} from "@/components/navigation/nav-config";
import {
	type PresentedNavGroup,
	type PresentedNavItem,
	usePresentedNavGroups,
} from "@/components/navigation/use-nav-availability";
import { useNavBadges } from "@/components/navigation/use-nav-badges";
import { useStreakState } from "@/components/streaks/_api";
import { StreakModal } from "@/components/streaks/streak-modal";
import { cn } from "@/lib/cn";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { useAddMenuStore } from "@/stores/add-menu";
import { useLoginModalStore } from "@/stores/login-modal";
import { useSessionStore } from "@/stores/session";
import { BrandLogo } from "./brand-logo";
import { BrandSparkle } from "./brand-sparkle";

interface SidebarProps {
	isCollapsed: boolean;
	onToggleCollapsed: () => void;
	/**
	 * Можно ли переключать рельс. `false` — состояние навязано снаружи
	 * (воркспейс курса держит рельс свёрнутым, `useSidebarLayoutState(true)`
	 * делает `toggle` no-op'ом). Тогда кнопку сворачивания НЕ рендерим: иначе
	 * получается молчаливый no-op — кликабельный контрол с `aria-label`
	 * «Развернуть», который ничего не делает. По умолчанию `true` — все
	 * существующие вызовы поведения не меняют.
	 */
	canToggleCollapsed?: boolean;
}

export const Sidebar = forwardRef<HTMLElement, SidebarProps>(function Sidebar(
	{ isCollapsed, onToggleCollapsed, canToggleCollapsed = true },
	ref,
) {
	const { t } = useTranslation("layout");
	// Кнопка-«разводящая»: открывает попап «Что добавить?» (загрузить материал /
	// записать лекцию) — тот же AddMenuDialog, что и «+» в mobile bottom-nav.
	const openAddMenu = useAddMenuStore((s) => s.open);
	const streakQuery = useStreakState();
	const navBadges = useNavBadges();
	const tier = useSessionStore((s) => s.tier);
	// Продажа скрыта, пока админский тумблер не включит коммерцию на этой платформе.
	const commerceVisible = useCommerceVisible();
	const isAuthenticated = useSessionStore((s) => s.isAuthenticated);
	const currentStreak = streakQuery.data?.currentStreak ?? 0;
	const [streakOpen, setStreakOpen] = useState(false);
	// Группы проходят через карту доступности: выключенный модуль исчезает из
	// меню, аварийный остаётся видимым со знаком (матрица §6.1). Гость карты не
	// имеет — его группы проходят насквозь.
	const groups = usePresentedNavGroups(isAuthenticated ? SIDEBAR_GROUPS : GUEST_SIDEBAR_GROUPS);
	return (
		<>
			<aside
				ref={ref}
				aria-label={t("sidebar.ariaLabel")}
				data-collapsed={isCollapsed}
				className={cn(
					"fixed left-0 top-0 bottom-0 z-(--sh-z-chrome-top) hidden lg:flex",
					// Холодная панель-пульт чуть темнее контента; правый край — тонкое «ребро».
					"flex-col bg-sidebar text-sidebar-foreground",
					"border-r border-sidebar-border shadow-[1px_0_0_rgba(0,0,0,0.25)]",
					"transition-[width] duration-base ease-out",
					// Нижний отступ — на самой панели, а НЕ на последнем элементе.
					// Раньше его давал `mb-3` кнопки сворачивания, но в воркспейсе
					// курса состояние навязано снаружи и кнопки нет вовсе — стрик
					// оказывался прижат к нижней кромке экрана (жалоба владельца
					// 2026-08-18). Отступ, который зависит от того, какой
					// необязательный блок оказался последним, ломается при первом же
					// его скрытии.
					"pb-3",
					isCollapsed ? "w-16 px-2" : "w-[248px] px-3",
				)}
			>
				{/* Brand row */}
				<div
					className={cn(
						"flex items-center shrink-0 h-14",
						isCollapsed ? "justify-center" : "justify-between",
					)}
				>
					<Link
						to="/"
						className="flex items-center gap-3 focus-visible:outline-none cursor-pointer"
						aria-label={t("sidebar.brand")}
					>
						{/* Brand-mark — приподнятая тактильная плитка с ребром и бликом. */}
						<span
							className={cn(
								"inline-flex size-[34px] items-center justify-center rounded-[9px] shrink-0",
								"[background-image:linear-gradient(155deg,var(--primary-2),var(--primary))]",
								"shadow-[0_2px_0_var(--primary-edge),inset_0_1px_0_rgba(255,255,255,0.3),0_4px_10px_rgba(0,0,0,0.4)]",
							)}
						>
							<BrandLogo size={20} />
						</span>
						{!isCollapsed && (
							<span className="text-[17px] font-semibold tracking-tight leading-none text-(--sidebar-bright)">
								Study<span className="text-(--sidebar-accent-blue)">Hub</span>
							</span>
						)}
					</Link>
				</div>

				{/* Primary CTA — «Загрузить материал» для авторизованных, «Войти» для гостей. */}
				<div className="shrink-0 pb-4 pt-3">
					{isAuthenticated ? (
						isCollapsed ? (
							<IconTooltip label={t("sidebar.create")}>
								<Button
									variant="default"
									size="icon"
									onClick={openAddMenu}
									aria-label={t("sidebar.create")}
									className="w-full"
								>
									<IconPlus size={18} stroke={1.75} />
								</Button>
							</IconTooltip>
						) : (
							<Button
								variant="default"
								onClick={openAddMenu}
								className="w-full justify-center gap-2 h-10"
							>
								<IconPlus size={18} stroke={1.75} />
								<span>{t("sidebar.create")}</span>
							</Button>
						)
					) : isCollapsed ? (
						<IconTooltip label={t("topbar.login")}>
							<Button
								variant="default"
								size="icon"
								className="w-full"
								aria-label={t("topbar.login")}
								onClick={() => useLoginModalStore.getState().open()}
							>
								<IconArrowRight size={18} stroke={1.75} />
							</Button>
						</IconTooltip>
					) : (
						<Button
							variant="default"
							className="w-full justify-center gap-2 h-10"
							onClick={() => useLoginModalStore.getState().open()}
						>
							<span>{t("topbar.login")}</span>
							<IconArrowRight size={16} stroke={1.75} />
						</Button>
					)}
				</div>

				{/* Nav groups */}
				<nav
					aria-label={t("sidebar.navAriaLabel")}
					className="sidebar-scrollarea flex-1 min-h-0 overflow-y-auto overscroll-contain -mx-1 px-1"
				>
					{groups.map((group) => (
						<SidebarGroup
							key={group.id}
							group={group}
							isCollapsed={isCollapsed}
							navBadges={navBadges}
						/>
					))}

					{/* Promo card (Free only). Скрыт, когда коммерция выключена: в
					    мобильной сборке до заведения IAP продавать нельзя (guideline
					    3.1.1), а карточка «перейти на Plus» — это и есть продажа. */}
					{isAuthenticated && !isCollapsed && tier === "free" && commerceVisible && (
						<div className="mt-2 mb-1 rounded-[11px] bg-white/[0.045] border border-white/8 p-3">
							<div className="flex items-center gap-2 mb-1">
								<IconSparkles size={14} stroke={1.75} className="text-(--sidebar-accent-blue)" />
								<span className="text-[13px] font-semibold text-(--sidebar-bright)">
									{t("promo.plusTitle")}
								</span>
							</div>
							<p className="text-[11px] text-sidebar-foreground/70 mb-2.5 leading-relaxed">
								{t("promo.plusBody")}
							</p>
							<Button asChild variant="default" size="sm" className="w-full">
								<Link to="/pricing">{t("promo.plusCta")}</Link>
							</Button>
						</div>
					)}
				</nav>

				{/* Streak — ЗАКРЕПЛЁН внизу (shrink-0, вне скролла nav): всегда виден
				    при прокрутке пунктов меню. Клик открывает StreakModal. Развёрнуто —
				    карточка, свёрнуто — иконка-кнопка с tooltip. */}
				{isAuthenticated && currentStreak > 0 && (
					<div className="shrink-0 pt-1.5">
						{isCollapsed ? (
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type="button"
										onClick={() => setStreakOpen(true)}
										aria-label={t("streak.days", { count: currentStreak })}
										className={cn(
											"cat-streak mx-auto flex size-9 items-center justify-center rounded-[9px] cursor-pointer",
											"bg-white/[0.045] border border-white/8 hover:bg-white/[0.07] active:translate-y-px",
										)}
									>
										<IconFlame size={16} stroke={1.75} className="text-[var(--cf)]" />
									</button>
								</TooltipTrigger>
								<TooltipContent side="right" className="tabular-nums">
									{t("streak.days", { count: currentStreak })}
								</TooltipContent>
							</Tooltip>
						) : (
							<button
								type="button"
								onClick={() => setStreakOpen(true)}
								aria-label={t("streak.openAria", { defaultValue: "Открыть детали серии" })}
								className={cn(
									"cat-streak w-full rounded-[11px] px-3 py-2.5 text-left cursor-pointer",
									"flex items-center gap-2.5 transition-transform duration-instant active:translate-y-px",
									"bg-white/[0.045] border border-white/8 hover:bg-white/[0.07]",
								)}
							>
								<span className="inline-flex size-7 items-center justify-center rounded-[8px] bg-[var(--cb)] shrink-0">
									<IconFlame size={16} stroke={1.75} className="text-[var(--cf)]" />
								</span>
								<div className="flex-1 min-w-0">
									<div className="text-[13px] font-semibold tabular-nums text-(--sidebar-bright) leading-tight">
										{t("streak.days", { count: currentStreak })}
									</div>
									<p className="text-[11px] text-sidebar-foreground/70 leading-tight mt-0.5">
										{t("streak.encouragement")}
									</p>
								</div>
							</button>
						)}
					</div>
				)}

				{/* Collapse toggle — тихая нижняя кнопка с верхним «ребром».
				    Скрыта, когда состояние навязано снаружи (воркспейс курса):
				    кнопка-no-op хуже отсутствующей кнопки. */}
				{canToggleCollapsed && (
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type="button"
								onClick={onToggleCollapsed}
								className={cn(
									"shrink-0 mt-1.5 h-9 rounded-[9px] cursor-pointer",
									"flex items-center text-sidebar-foreground/70",
									"border-t border-white/[0.06]",
									"hover:bg-white/[0.045] hover:text-sidebar-accent-foreground",
									"transition-colors duration-fast text-[12.5px] active:translate-y-px",
									isCollapsed ? "justify-center" : "px-3 gap-2",
								)}
								aria-label={isCollapsed ? t("sidebar.expand") : t("sidebar.collapseAria")}
								aria-expanded={!isCollapsed}
							>
								{isCollapsed ? (
									<IconChevronRight size={18} stroke={1.75} />
								) : (
									<>
										<IconChevronLeft size={18} stroke={1.75} />
										<span>{t("sidebar.collapse")}</span>
									</>
								)}
							</button>
						</TooltipTrigger>
						<TooltipContent side="right">
							{isCollapsed ? t("tooltips.expandSidebar") : t("tooltips.collapseSidebar")}
						</TooltipContent>
					</Tooltip>
				)}
			</aside>
			<StreakModal open={streakOpen} onClose={() => setStreakOpen(false)} />
		</>
	);
});

interface SidebarGroupProps {
	group: PresentedNavGroup;
	isCollapsed: boolean;
	navBadges: Record<string, number>;
}

/**
 * Группа пунктов навигации. Группы с `collapsible` (сейчас — «Инструменты»)
 * сразу раскрыты: маленькая eyebrow-подпись + chevron для сворачивания.
 * Авто-раскрытие, когда активный роут внутри.
 *
 * В свёрнутом sidebar (icon-only, 64px) заголовков групп нет — пункты подряд.
 */
function SidebarGroup({ group, isCollapsed, navBadges }: SidebarGroupProps): ReactElement {
	const { t } = useTranslation("layout");
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const [isOpen, setIsOpen] = useState(true);
	const expanded =
		isOpen || group.items.some((item) => isNavItemActive(pathname, item, ALL_NAV_ITEMS));

	const items = (
		<div className="space-y-0.5">
			{group.items.map((item) => (
				<SidebarNavItem
					key={item.to}
					item={item}
					isCollapsed={isCollapsed}
					badgeOverride={navBadges[item.to]}
				/>
			))}
		</div>
	);

	if (group.collapsible && group.eyebrowKey && !isCollapsed) {
		return (
			<Collapsible open={expanded} onOpenChange={setIsOpen} className="mb-1" data-group={group.id}>
				<CollapsibleTrigger
					className="flex w-full cursor-pointer items-center gap-1 px-3 pt-3.5 pb-1.5 text-left"
					aria-label={t(group.eyebrowKey)}
				>
					<span className="min-w-0 flex-1 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-sidebar-foreground/55">
						{t(group.eyebrowKey)}
					</span>
					<IconChevronDown
						size={12}
						stroke={2}
						aria-hidden="true"
						className={cn(
							"shrink-0 text-sidebar-foreground/55 transition-transform duration-base",
							!expanded && "-rotate-90",
						)}
					/>
				</CollapsibleTrigger>
				<CollapsibleContent className="pt-0.5">{items}</CollapsibleContent>
			</Collapsible>
		);
	}

	return (
		<div className="mb-1">
			{group.eyebrowKey && !isCollapsed && (
				<div className="px-3 pt-3.5 pb-1.5 text-[10.5px] uppercase tracking-[0.16em] font-semibold text-sidebar-foreground/55">
					{t(group.eyebrowKey)}
				</div>
			)}
			{group.eyebrowKey && isCollapsed && (
				<div className="my-2.5 mx-2 border-t border-white/8" aria-hidden="true" />
			)}
			{items}
		</div>
	);
}

interface SidebarNavItemProps {
	item: PresentedNavItem;
	isCollapsed: boolean;
	badgeOverride?: number;
}

function SidebarNavItem({ item, isCollapsed, badgeOverride }: SidebarNavItemProps): ReactElement {
	const { t } = useTranslation("layout");
	const Icon = item.icon;
	const router = useRouterState({ select: (s) => s.location.pathname });
	const isActive = isNavItemActive(router, item, ALL_NAV_ITEMS);
	const badge = badgeOverride ?? item.badge;
	const label = t(item.labelKey);

	// Hash-anchor ссылки (например, /#faq) рендерим нативным <a>,
	// чтобы браузер сам прокрутил к якорю — TanStack Router Link не поддерживает «#» в `to`.
	const isHashAnchor = item.to.includes("#");

	const inner = (
		<>
			{/* Боковой синий акцент активного пункта — только в развёрнутом режиме.
			    В свёрнутом полоску не рисуем (как в развёрнутом нет полоски слева
			    самого пункта), чтобы выделение было ровным квадратом. */}
			{isActive && !isCollapsed && (
				<span
					aria-hidden="true"
					className="absolute top-2 bottom-2 -left-3 w-[3px] rounded-r-[3px] bg-(--sidebar-accent-blue)"
				/>
			)}
			<Icon
				size={18}
				stroke={1.75}
				className={cn("shrink-0", isActive && "text-(--sidebar-icon-active)")}
			/>
			{!isCollapsed && (
				<>
					<span className="truncate flex-1">{label}</span>
					{item.accent === "sparkle" && <BrandSparkle size={12} />}
					<ModuleNavMarker presentation={item.presentation} />
					{typeof badge === "number" && badge > 0 && (
						<span
							className={cn(
								"text-[11px] font-semibold tabular-nums rounded-md px-1.5 py-px",
								// Приоритет («Обучение») — янтарная плитка; остальные тихие.
								item.badgeTone === "warning"
									? "tint-warning"
									: isActive
										? "bg-white/10 text-(--sidebar-accent-foreground)"
										: "bg-white/[0.07] text-sidebar-foreground/80",
							)}
						>
							{badge}
						</span>
					)}
				</>
			)}
			{/* В свёрнутом режиме точку-индикатор не рисуем — число
			    показывается в tooltip при наведении (ниже). */}
		</>
	);

	const containerClassName = cn(
		"group/nav relative flex items-center h-9 rounded-[9px] cursor-pointer",
		"text-[13.5px] transition-[background-color,color,transform] duration-fast",
		"active:translate-y-px",
		isCollapsed ? "w-9 mx-auto justify-center px-0" : "gap-[11px] px-[11px]",
		// Авария — приглушённый пункт: раздел на месте, но сейчас не работает.
		isDimmedPresentation(item.presentation) && "opacity-60",
		isActive
			? "font-medium text-(--sidebar-bright) [background-image:linear-gradient(180deg,var(--sidebar-item-active-from),var(--sidebar-item-active-to))] shadow-[0_2px_0_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.07)]"
			: "text-sidebar-foreground hover:bg-white/[0.045] hover:text-sidebar-accent-foreground",
	);

	const link = isHashAnchor ? (
		<a href={item.to} className={containerClassName} aria-label={isCollapsed ? label : undefined}>
			{inner}
		</a>
	) : (
		<Link
			to={item.to}
			preload="intent"
			className={containerClassName}
			aria-label={isCollapsed ? label : undefined}
			aria-current={isActive ? "page" : undefined}
		>
			{inner}
		</Link>
	);

	// В развёрнутом режиме название и число видны прямо в пункте — tooltip не нужен.
	// Подпункты (Библиотека → Папки, Избранное). Рисуем только в развёрнутом
	// сайдбаре и только когда ветка активна: постоянно раскрытое дерево съедает
	// высоту, а в свёрнутом режиме показывать нечего — там одни иконки.
	//
	// Активность ветки считаем по родителю И детям: на `/favorites` подсвечен
	// ребёнок, и без этого условия ветка схлопнулась бы прямо под курсором.
	const children = item.children ?? [];
	const branchActive =
		isActive || children.some((child) => isNavItemActive(router, child, ALL_NAV_ITEMS));

	if (!isCollapsed) {
		if (children.length === 0 || !branchActive) return link;
		return (
			<>
				{link}
				<div className="mt-0.5 space-y-0.5 pl-[22px]">
					{children.map((child) => (
						<SidebarNavItem key={child.to} item={child} isCollapsed={false} />
					))}
				</div>
			</>
		);
	}

	const tooltipLabel = typeof badge === "number" && badge > 0 ? `${label} · ${badge}` : label;

	return (
		<Tooltip>
			<TooltipTrigger asChild>{link}</TooltipTrigger>
			<TooltipContent side="right" className="tabular-nums">
				{tooltipLabel}
			</TooltipContent>
		</Tooltip>
	);
}
